import { getLocalTimeZone, today } from '@internationalized/date';
import { isDefined } from '@sgme/fp';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';

import { useDatePickerCalendar } from '../Calendar/CalendarContext';
import { useDatePickerConfig } from '../contexts/ConfigContext';
import { useDatePickerActions } from '../Dropdown/DropdownContext';
import { useDateFormatter } from '../hooks/useDateFormatter';
import { useDatePickerValidation } from './ValidationContext';

/**
 * Input Component for SgDatePicker
 *
 * VALUE MANAGEMENT FLOW:
 * ======================
 * This component manages a controlled input with a validation and restoration mechanism.
 *
 * 1. USER TYPING (handleChange):
 *    - User types in the input
 *    - `localValue` is updated immediately (real-time display)
 *    - Parent is notified via `onInputChanged` (optional)
 *    - `lastValidInputValue` is NOT modified (keeps the last validated value)
 *
 * 2. SUBMISSION (handleSubmit, called on blur or Enter):
 *    - The value is parsed and validated
 *    - Parent is notified via `onDateChange` with the parsed value
 *    - In controlled mode, we send to parent even if invalid
 *    - Parent decides whether to accept or reject the value
 *
 * 3. PARENT RESPONSE (useEffect on inputValue):
 *    - If parent accepts: `inputValue` changes → `localValue` AND `lastValidInputValue` are updated
 *    - If parent rejects: `inputValue` doesn't change → `lastValidInputValue` keeps the old value
 *
 * 4. RESTORATION (handleBlur):
 *    - After handleSubmit, we compare `localValue` with `lastValidInputValue`
 *    - If different: parent rejected → restore `lastValidInputValue` in the input
 *    - Result: input always displays a valid value after blur
 *
 * EXAMPLE:
 * --------
 * - Initial value: "01/01/2024" (inputValue = "01/01/2024", lastValidInputValue = "01/01/2024")
 * - User types "32/01/2024" → localValue = "32/01/2024", lastValidInputValue = "01/01/2024"
 * - User leaves the field (blur) → handleSubmit sends "32/01/2024" to parent
 * - Parent rejects (invalid date) → inputValue remains "01/01/2024"
 * - handleBlur detects localValue ≠ lastValidInputValue → restores "01/01/2024" in the input
 */
export const Input = (props: React.HTMLAttributes<HTMLDivElement> & { ref?: React.Ref<HTMLDivElement> }) => {
  const { ref, ...restProps } = props;
  const { inputId, inputRef, placeholder, disabled, readonly, isControlled, focusManagement, color } =
    useDatePickerConfig();
  const { inputValue, calendarState } = useDatePickerCalendar();
  const validation = useDatePickerValidation();
  const { onDateChange, onFocus, onBlur, onOpen, onClose, onInputChanged } = useDatePickerActions();
  const dateFormatter = useDateFormatter();

  // Local input value management
  // -----------------------------
  // `localValue`: value displayed in the input, updated on every user keystroke
  const [localValue, setLocalValue] = useState(inputValue);

  // `lastValidInputValue`: reference to the last value validated by the parent
  // This value is only updated when `inputValue` changes (parent validation)
  // It serves as a "restoration point" if the user enters an invalid value
  const lastValidInputValue = useRef(inputValue);

  // Synchronization with the `inputValue` prop (from parent)
  // When parent updates `inputValue` (after validation), we update:
  // 1. The value displayed in the input (localValue)
  // 2. The last valid reference value (lastValidInputValue)
  useEffect(() => {
    setLocalValue(inputValue);
    lastValidInputValue.current = inputValue;
  }, [inputValue]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // Prevent editing in readonly mode
    if (readonly) {
      if (event.key.length === 1 || event.key === 'Backspace' || event.key === 'Delete') {
        event.preventDefault();
      }

      return;
    }

    // Handle Enter key: submit the value before delegating to focus management
    if (event.key === 'Enter') {
      event.currentTarget.blur();
      return;
    }

    // Delegate keyboard navigation to focus management
    focusManagement?.handleInputKeyDown(event, {
      onOpen: (shouldFocusCalendar) => onOpen?.(shouldFocusCalendar),
      onClose: () => {
        event.currentTarget.blur();
        onClose?.();
      },
      onEscape: () => {
        // Immediately restore the last valid value (cancel current modifications)
        setLocalValue(lastValidInputValue.current);

        // Lose focus to trigger blur
        (event.target as HTMLInputElement).blur();
      },
    });
  };

  const handleFocusInput = (event: React.FocusEvent<HTMLInputElement>) => {
    onFocus?.(event);
    event.target.select();
  };

  const handleBlur = () => {
    // Submit the current value to the parent for validation
    handleSubmit(localValue);

    // Restoration mechanism:
    // If after handleSubmit, the local value differs from the last valid value,
    // it means the parent didn't accept the modification (inputValue hasn't changed).
    // In that case, we restore the last valid value in the input.
    // This prevents the field from staying empty or with an invalid value after blur.
    if (localValue !== lastValidInputValue.current) {
      setLocalValue(lastValidInputValue.current);
    }
    onBlur?.();
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!readonly) {
      // Update local value on every user keystroke
      // This value is NOT yet validated by the parent
      setLocalValue(event.target.value);
      // Optional notification to parent that input has changed
      onInputChanged?.(event.target.value);
    }
  };

  const handleSubmit = (value: string) => {
    if (!isControlled) {
      validation.clearValidation();
    }

    if (value === inputValue) {
      return;
    }

    if (!value || value.trim() === '') {
      if (!isControlled) {
        calendarState.setFocusedDate(today(getLocalTimeZone()));
      }

      onDateChange?.('');

      return;
    }

    const validationResult = validation.validateDateInput(value);

    if (!validationResult.isValid) {
      if (isControlled) {
        const newDate =
          validationResult.parsedDate !== undefined ? validationResult.parsedDate.toString() : value.trim();
        onDateChange?.(newDate);
      } else {
        validation.setValidation(validationResult.state, validationResult.message);
      }

      return;
    }

    const parsedDate = dateFormatter.parse(value.trim());

    onDateChange?.(parsedDate?.toString() || value.trim());
    onClose?.();
  };

  return (
    <div ref={ref} className="flex-grow-1 w-100" style={{ minWidth: 0, display: 'flex' }} {...restProps}>
      <input
        id={inputId}
        ref={inputRef}
        type="text"
        value={localValue}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readonly}
        onKeyDown={handleKeyDown}
        onChange={handleChange}
        onFocus={handleFocusInput}
        onBlur={handleBlur}
        className={clsx(
          isDefined(color) && `text-${color}`,
          'mt-1 text-primary py-0 bg-transparent border-0 flex-grow-1',
        )}
        style={{
          outline: 'none',
          cursor: readonly || disabled ? 'default' : 'text',
          pointerEvents: 'auto',
        }}
        autoComplete="off"
      />
    </div>
  );
};

Input.displayName = 'Input';
